/**
 * SolarHistoryPage — kuu-tasandi PV (päike) ajalugu kõigil parkidel.
 * Tabeli kuju on inspireeritud Tulu ajaloo lehest (EconomicsHistoryPage).
 * Andmeallikas: /api/solar/monthly (Influx pv_meter energy_import delta).
 */

import { useState, useEffect, useMemo } from 'react';

const PERIODS = [
  { id: '12m', label: '12 kuud' },
  { id: '24m', label: '24 kuud' },
  { id: 'this_year', label: 'See aasta' },
  { id: 'all', label: 'Kogu ajalugu' },
  { id: 'custom', label: 'Vali' },
];

function ymStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function computeRange(period, customFrom, customTo) {
  const d = new Date();
  const curYM = ymStr(d);
  if (period === 'custom') {
    if (customFrom && customTo) return { from: customFrom, to: customTo };
    return null;
  }
  if (period === '12m') {
    const start = new Date(d.getFullYear(), d.getMonth() - 11, 1);
    return { from: ymStr(start), to: curYM };
  }
  if (period === '24m') {
    const start = new Date(d.getFullYear(), d.getMonth() - 23, 1);
    return { from: ymStr(start), to: curYM };
  }
  if (period === 'this_year') {
    return { from: `${d.getFullYear()}-01`, to: curYM };
  }
  if (period === 'all') {
    return { from: '2024-01', to: curYM };
  }
  return null;
}

function fmtKwh(n) {
  if (n == null || isNaN(n)) return '—';
  const v = Number(n);
  if (Math.abs(v) >= 1000) return `${(v / 1000).toFixed(2)} MWh`;
  return `${v.toFixed(1)} kWh`;
}

const MONTH_NAMES_ET = ['Jaan', 'Veebr', 'Märts', 'Aprill', 'Mai', 'Juuni',
                        'Juuli', 'Aug', 'Sept', 'Okt', 'Nov', 'Dets'];

function fmtMonth(ym) {
  const [y, m] = ym.split('-').map(Number);
  return `${MONTH_NAMES_ET[m - 1]} ${y}`;
}

export function SolarHistoryPage() {
  const [period, setPeriod] = useState('12m');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [expandedMonth, setExpandedMonth] = useState(null);

  const range = useMemo(() => computeRange(period, customFrom, customTo), [period, customFrom, customTo]);

  const fetchData = () => {
    if (!range) return;
    setLoading(true);
    setErr(null);
    fetch(`/api/solar/monthly?from=${range.from}&to=${range.to}`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(d => setData(d))
      .catch(e => { setErr(e.message); setData(null); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [period, customFrom, customTo]);

  // Perioodi-kokkuvõte (kogu vahemik kokku)
  const periodTotal = useMemo(() => {
    if (!data?.months) return null;
    let total = 0;
    let maxMonth = null;
    let maxMonthValue = 0;
    for (const m of data.months) {
      total += m.total_kwh || 0;
      if ((m.total_kwh || 0) > maxMonthValue) {
        maxMonthValue = m.total_kwh;
        maxMonth = m.month;
      }
    }
    return { total, months_count: data.months.length, max_month: maxMonth, max_month_kwh: maxMonthValue };
  }, [data]);

  // Reverse järjekord — uusim kuu kõige üleval
  const monthsDesc = useMemo(() => {
    if (!data?.months) return [];
    return [...data.months].sort((a, b) => b.month.localeCompare(a.month));
  }, [data]);

  // Per-park kokku üle kogu perioodi (laienenud kuu sees näitamiseks)
  const parkTotalsBySlot = useMemo(() => {
    if (!data?.months) return {};
    const out = {};
    for (const m of data.months) {
      for (const p of m.parks || []) {
        if (!out[p.slot]) out[p.slot] = { slot: p.slot, name: p.name, kwh: 0 };
        out[p.slot].kwh += p.kwh || 0;
      }
    }
    return out;
  }, [data]);

  const parkTotals = useMemo(() => Object.values(parkTotalsBySlot).sort((a, b) => a.slot - b.slot), [parkTotalsBySlot]);

  return (
    <div style={{ padding: '20px 24px', maxWidth: 1400, margin: '0 auto' }}>
      <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 16 }}>Päikese ajalugu — kuude kaupa</h1>

      {/* Perioodi valik */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {PERIODS.map(p => (
          <button
            key={p.id} onClick={() => setPeriod(p.id)}
            style={{
              padding: '6px 14px', borderRadius: 4,
              border: '1px solid #ddd',
              background: period === p.id ? '#11093E' : '#fff',
              color: period === p.id ? '#fff' : '#333',
              fontWeight: 500, cursor: 'pointer', fontSize: 13,
            }}
          >{p.label}</button>
        ))}
        {period === 'custom' && (
          <>
            <input type="month" value={customFrom} onChange={e => setCustomFrom(e.target.value)} style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid #ddd' }} />
            <span style={{ alignSelf: 'center' }}>—</span>
            <input type="month" value={customTo} onChange={e => setCustomTo(e.target.value)} style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid #ddd' }} />
          </>
        )}
      </div>

      {/* Perioodi-kokkuvõte */}
      {periodTotal && (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 24,
          background: '#F9FAF9', padding: 16, borderRadius: 8, border: '1px solid #e0e0e0',
        }}>
          <div>
            <div style={{ fontSize: 12, color: '#8C8C8C', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Tootmine kokku</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#d97706' }}>{fmtKwh(periodTotal.total)}</div>
            <div style={{ fontSize: 11, color: '#8C8C8C' }}>{periodTotal.months_count} kuud</div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#8C8C8C', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Parim kuu</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#15803d' }}>{periodTotal.max_month ? fmtMonth(periodTotal.max_month) : '—'}</div>
            <div style={{ fontSize: 11, color: '#8C8C8C' }}>{fmtKwh(periodTotal.max_month_kwh)}</div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#8C8C8C', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Keskmine kuus</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#11093E' }}>{fmtKwh(periodTotal.months_count > 0 ? periodTotal.total / periodTotal.months_count : 0)}</div>
            <div style={{ fontSize: 11, color: '#8C8C8C' }}>üle perioodi</div>
          </div>
        </div>
      )}

      {err && (
        <div style={{ padding: 12, background: '#ffebee', color: '#c62828', borderRadius: 4, marginBottom: 16 }}>
          Viga: {err}
        </div>
      )}

      {loading && <div style={{ padding: 12, color: '#666' }}>Laeb…</div>}

      {/* Per-park kokku */}
      {parkTotals.length > 0 && (
        <div style={{ marginBottom: 24, background: '#fff', borderRadius: 6, border: '1px solid #e0e0e0', overflow: 'hidden' }}>
          <div style={{ padding: '10px 12px', background: '#f5f5f5', borderBottom: '1px solid #ccc', fontWeight: 600 }}>
            Pargid kokku üle perioodi
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', padding: 12, gap: 8 }}>
            {parkTotals.map(p => (
              <div key={p.slot} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', background: '#fafafa', borderRadius: 4, fontSize: 13 }}>
                <span style={{ color: '#666' }}>{p.slot}. {p.name}</span>
                <span style={{ fontWeight: 600, color: '#d97706' }}>{fmtKwh(p.kwh)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Kuu-tabel */}
      {monthsDesc.length > 0 && (
        <div style={{ background: '#fff', borderRadius: 6, border: '1px solid #e0e0e0', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: '#f5f5f5' }}>
              <tr>
                <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #ccc', width: 140 }}>Kuu</th>
                <th style={{ padding: '10px 12px', textAlign: 'right', borderBottom: '1px solid #ccc' }}>Tootmine kokku</th>
                <th style={{ padding: '10px 12px', textAlign: 'right', borderBottom: '1px solid #ccc' }}>Pargi keskmine</th>
                <th style={{ padding: '10px 12px', textAlign: 'right', borderBottom: '1px solid #ccc' }}>Parkide arv</th>
                <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '1px solid #ccc', width: 90 }}>Pargid</th>
              </tr>
            </thead>
            <tbody>
              {monthsDesc.map(m => (
                <MonthRow
                  key={m.month}
                  month={m}
                  expanded={expandedMonth === m.month}
                  onToggle={() => setExpandedMonth(expandedMonth === m.month ? null : m.month)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {monthsDesc.length === 0 && !loading && (
        <div style={{ padding: 24, textAlign: 'center', color: '#888' }}>Valitud perioodis pole andmeid.</div>
      )}
    </div>
  );
}

function MonthRow({ month, expanded, onToggle }) {
  const validParks = (month.parks || []).filter(p => p.kwh != null && p.kwh > 0);
  const avgPerPark = validParks.length > 0 ? month.total_kwh / validParks.length : 0;
  return (
    <>
      <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
        <td style={{ padding: '8px 12px', fontWeight: 500 }}>{fmtMonth(month.month)}</td>
        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: '#d97706' }}>{fmtKwh(month.total_kwh)}</td>
        <td style={{ padding: '8px 12px', textAlign: 'right', color: '#666' }}>{fmtKwh(avgPerPark)}</td>
        <td style={{ padding: '8px 12px', textAlign: 'right', color: '#666' }}>{validParks.length}</td>
        <td style={{ padding: '8px 12px', textAlign: 'center' }}>
          <button onClick={onToggle} style={{ background: 'none', border: '1px solid #ccc', borderRadius: 4, padding: '2px 10px', cursor: 'pointer', fontSize: 12 }}>
            {expanded ? '▾ Peida' : `▸ ${month.parks?.length || 0}`}
          </button>
        </td>
      </tr>
      {expanded && (month.parks || []).map(p => (
        <tr key={p.slot} style={{ background: '#fafafa', fontSize: 12 }}>
          <td style={{ padding: '6px 12px 6px 32px', color: '#666' }}>{p.slot}. {p.name}</td>
          <td style={{ padding: '6px 12px', textAlign: 'right', color: '#d97706', fontWeight: 500 }}>{fmtKwh(p.kwh)}</td>
          <td colSpan={3}></td>
        </tr>
      ))}
    </>
  );
}

export default SolarHistoryPage;
